import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

interface SavedAssignment {
  id: string;
  assignmentId: string;
  status: string; // not_applied | applied | won | lost
  appliedDate: string | null;
  outcomeDate: string | null;
  offeredPrice: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  assignmentTitle: string;
  assignmentLocation: string | null;
  assignmentStatus: string; // active | closed
  matchScore: string | null;
  heuristicScore: string | null;
  teamFitScore: string | null;
  matchId: string | null;
}

export function useSavedAssignments() {
  return useQuery({
    queryKey: ['saved-assignments'],
    queryFn: () => apiFetch<SavedAssignment[]>('/saved-assignments')
  });
}

export function useSaveAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      status,
      notes
    }: {
      assignmentId: string;
      status?: string;
      notes?: string;
    }) =>
      apiFetch<SavedAssignment>('/saved-assignments', {
        method: 'POST',
        body: JSON.stringify({ assignmentId, status, notes })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-assignments'] });
    }
  });
}

export function useUpdateSavedAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      id: string;
      status?: string;
      notes?: string;
      offeredPrice?: string;
      appliedDate?: string;
      outcomeDate?: string;
    }) => {
      const { id, ...body } = payload;
      return apiFetch<null>(`/saved-assignments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-assignments'] });
    }
  });
}

export function useRemoveSavedAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<null>(`/saved-assignments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-assignments'] });
    }
  });
}

export type { SavedAssignment };
